// The measure tool's sources and layers, bottom to top. Colours and widths follow
// the design: a green line on a white casing, the route chevron sprite the distance
// markers use, the planner's start control point, and dashed guides for the locks.

export const MEASURE_GREEN = '#4BC263';
export const MEASURE_YELLOW = '#FDDF00';
export const MEASURE_INK = '#17181D';
export const MY_LOCATION_BLUE = '#2B7BE4';
/** The start sprite's size (82 px at 1), half as big again as the planner draws it, so it is easy to grab. */
export const START_ICON_SIZE = 0.375;

export const MEASURE_SOURCES = [
  'measure-band', 'measure-band-line', 'measure-guides', 'measure-straight', 'measure-route',
  'measure-preview', 'measure-ticks', 'measure-points',
] as const;
export type MeasureSourceId = typeof MEASURE_SOURCES[number];

const ROUND = { 'line-cap': 'round', 'line-join': 'round' } as const;

/** Point features in `measure-points` say what they are with `kind`. */
export type MeasurePointKind = 'my-location' | 'start-pin' | 'route-start' | 'preview' | 'profile' | 'tip';
const kind = (k: MeasurePointKind | MeasurePointKind[]) =>
  (Array.isArray(k) ? ['in', ['get', 'kind'], ['literal', k]] : ['==', ['get', 'kind'], k]);

export function measureLayers(bandFillMinZoom = 0): any[] {
  return [
    // The ±tolerance annulus after a range placement; below bandFillMinZoom it would be
    // thinner than 2 px, so a 2 px stroke along the circle stands in for it.
    { id: 'measure-band-fill', type: 'fill', source: 'measure-band', minzoom: bandFillMinZoom,
      paint: { 'fill-color': MEASURE_YELLOW, 'fill-opacity': 0.45 } },
    { id: 'measure-band-line', type: 'line', source: 'measure-band-line', maxzoom: Math.max(bandFillMinZoom, 0.01),
      layout: ROUND, paint: { 'line-color': MEASURE_YELLOW, 'line-opacity': 0.45, 'line-width': 2 } },
    // Lock circle, lock ray and, in route mode, the direct line between the snapped ends.
    { id: 'measure-guides', type: 'line', source: 'measure-guides',
      paint: { 'line-color': MEASURE_INK, 'line-opacity': 0.7, 'line-width': 1.5, 'line-dasharray': [4, 3.3] } },
    { id: 'measure-straight-casing', type: 'line', source: 'measure-straight', layout: ROUND,
      paint: { 'line-color': '#FFFFFF', 'line-opacity': 0.8, 'line-width': 7 } },
    { id: 'measure-straight', type: 'line', source: 'measure-straight', layout: ROUND,
      paint: { 'line-color': MEASURE_GREEN, 'line-width': 4 } },
    { id: 'measure-route-casing', type: 'line', source: 'measure-route', layout: ROUND,
      paint: { 'line-color': '#FFFFFF', 'line-opacity': 0.85, 'line-width': 8 } },
    { id: 'measure-route', type: 'line', source: 'measure-route', layout: ROUND,
      paint: { 'line-color': MEASURE_GREEN, 'line-width': 6 } },
    { id: 'measure-preview-casing', type: 'line', source: 'measure-preview', layout: ROUND,
      paint: { 'line-color': '#FFFFFF', 'line-opacity': 0.7, 'line-width': 6 } },
    { id: 'measure-preview', type: 'line', source: 'measure-preview',
      paint: { 'line-color': MEASURE_GREEN, 'line-width': 3, 'line-dasharray': [2.7, 2] } },
    // The route chevron sprite is drawn pointing east, so it turns by bearing − 90.
    { id: 'measure-ticks', type: 'symbol', source: 'measure-ticks',
      layout: {
        'icon-image': 'hike-arrow-icon',
        'icon-size': 0.15,
        'icon-rotate': ['get', 'rotation'],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: { 'icon-color': '#FFFFFF' } },
    { id: 'measure-my-location-halo', type: 'circle', source: 'measure-points', filter: kind('my-location'),
      paint: { 'circle-radius': 20, 'circle-color': MY_LOCATION_BLUE, 'circle-opacity': 0.16 } },
    { id: 'measure-my-location', type: 'circle', source: 'measure-points', filter: kind('my-location'),
      paint: { 'circle-radius': 7.5, 'circle-color': MY_LOCATION_BLUE, 'circle-stroke-color': '#FFFFFF', 'circle-stroke-width': 2.5 } },
    // A My-location start is marked by a yellow ring round the location dot.
    { id: 'measure-my-location-ring', type: 'circle', source: 'measure-points', filter: kind('my-location'),
      paint: { 'circle-radius': 12.25, 'circle-opacity': 0, 'circle-stroke-color': MEASURE_YELLOW, 'circle-stroke-width': 3.5 } },
    { id: 'measure-start-pin', type: 'symbol', source: 'measure-points', filter: kind('start-pin'),
      layout: { 'icon-image': 'hike-control-point-start', 'icon-size': START_ICON_SIZE, 'icon-anchor': 'center',
        'icon-allow-overlap': true, 'icon-ignore-placement': true } },
    { id: 'measure-route-start', type: 'circle', source: 'measure-points', filter: kind('route-start'),
      paint: { 'circle-radius': 6, 'circle-color': MEASURE_GREEN, 'circle-stroke-color': '#FFFFFF', 'circle-stroke-width': 3 } },
    { id: 'measure-preview-point', type: 'circle', source: 'measure-points', filter: kind('preview'),
      paint: { 'circle-radius': 7, 'circle-color': MEASURE_YELLOW, 'circle-opacity': 0.35, 'circle-stroke-color': MEASURE_YELLOW, 'circle-stroke-width': 2 } },
    { id: 'measure-profile-point', type: 'circle', source: 'measure-points', filter: kind('profile'),
      paint: { 'circle-radius': 6, 'circle-color': '#FFFFFF', 'circle-stroke-color': MEASURE_INK, 'circle-stroke-width': 2 } },
    // The exact measured spot, under the pin's tip.
    { id: 'measure-tip', type: 'circle', source: 'measure-points', filter: kind('tip'),
      paint: { 'circle-radius': 2.25, 'circle-color': '#FFFFFF', 'circle-stroke-color': MEASURE_INK, 'circle-stroke-width': 1.5 } },
  ];
}

export const MEASURE_LAYER_IDS: string[] = measureLayers().map((layer) => layer.id);

export const EMPTY_COLLECTION = Object.freeze({ type: 'FeatureCollection', features: [] as any[] });
